import type { QueryClient } from "@tanstack/react-query";
import type { ReportData } from "~/types/global";
import { useReportListStore } from "~/store/reportListStore";
import { REPORTS_CLIENT } from "~/services/clients/reports.client";

/**
 * Resolução do envelope de DEK de um relatório JÁ EXISTENTE.
 *
 * Extraído de `useSectionPersistence` para ser reusado pelo save de parcial
 * (épico #21124): os dois precisam reencriptar conteúdo com o DEK **do próprio
 * relatório** — nunca gerando um DEK novo (`generateEnvelope()`) e nunca
 * encriptando direto com a secret key do usuário. Um reset de senha só faz
 * re-wrap do DEK (`docs/vault/06-data-flows/re-encryption.md`); conteúdo
 * encriptado com a secret key dessincronizaria do resto do relatório no próximo
 * reset, porque nada o re-encriptaria.
 *
 * Três fontes, em ordem de custo:
 *   1. `reportListStore` (populado pela query `["reports","list"]`);
 *   2. cache `["reports","details",id]` (sempre presente numa tela de detalhe);
 *   3. `GET /get-one-report/{id}` — fetch ativo leve.
 *
 * `user_wrapped_dek` / `user_dek_iv` são campos de passagem em texto claro na
 * resposta, então o fallback (3) não precisa de pipeline de decriptação.
 * Devolve `null` quando nenhuma fonte tem o envelope — o caller então PULA a
 * operação em vez de cair para uma chave insegura.
 */
export interface ReportDekEnvelope {
  wrappedDek: string;
  dekIv: string;
}

export async function resolveReportDekEnvelope(
  userReportsId: string,
  queryClient: Pick<QueryClient, "getQueryData">,
): Promise<ReportDekEnvelope | null> {
  const listed = useReportListStore
    .getState()
    .list.find((item) => item?.user_reports_id === userReportsId);
  const detailCache = queryClient.getQueryData<ReportData>([
    "reports",
    "details",
    userReportsId,
  ]);

  let wrappedDek = listed?.user_wrapped_dek ?? detailCache?.user_wrapped_dek;
  let dekIv = listed?.user_dek_iv ?? detailCache?.user_dek_iv;

  if (!wrappedDek || !dekIv) {
    try {
      const response = await REPORTS_CLIENT.get<{
        user_wrapped_dek?: string | null;
        user_dek_iv?: string | null;
      }>(`/get-one-report/${userReportsId}`);
      wrappedDek = response.data?.user_wrapped_dek ?? undefined;
      dekIv = response.data?.user_dek_iv ?? undefined;
    } catch (err) {
      // Identificadores apenas — nunca conteúdo.
      console.warn(
        `[resolveReportDekEnvelope] Re-fetch do envelope falhou para ${userReportsId}: ` +
          (err instanceof Error ? err.message : "erro desconhecido"),
      );
    }
  }

  if (!wrappedDek || !dekIv) return null;
  return { wrappedDek, dekIv };
}
